import { useEffect, useState } from "react";
import LandingPage from "../LandingPage";
import AIDocumentation from "./Topics/AIDocumentation";
import MedicalTranscription from "./Topics/MedicalTranscription";
import ClinicalSummaries from "./Topics/ClinicalSummaries";
import SmartEHRIntegration from "./Topics/SmartEHRIntegration";
import HealthcareAnalytics from "./Topics/HealthcareAnalytics";
import PricingPageV2 from "./Topics/PricingPageV2";
import SecurityPrivacyPolicy from "./policy/SecurityPrivacyPolicy";
import TermsOfUse from "./policy/TermsOfUse";
import ComplianceFramework from "./policy/ComplianceFramework";
import AccessibilityStatement from "./policy/AccessibilityStatement";

type Route =
  | "home"
  | "ai-documentation"
  | "medical-transcription"
  | "clinical-summaries"
  | "pricing"
  | "smart-ehr-integration"
  | "healthcare-analytics"
  | "privacy"
  | "terms"
  | "compliance"
  | "accessibility";

interface RouterProps {
  initialRoute?: Route;
}

const Router: React.FC<RouterProps> = ({ initialRoute = "home" }) => {
  // Utility function to scroll to top with multiple fallback methods
  const scrollToTop = () => {
    // Method 1: Modern smooth scrolling
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch (error) {
      // Fallback for browsers that don't support smooth scrolling
      window.scrollTo(0, 0);
    }

    // Method 2: Direct DOM manipulation for maximum compatibility
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // Method 3: Handle any scrollable containers
    const scrollableElements = document.querySelectorAll("[data-scrollable]");
    scrollableElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.scrollTop = 0;
      }
    });
  };

  const [currentRoute, setCurrentRoute] = useState<Route>(() => {
    // Initialize route based on current path
    const path = window.location.pathname;
    if (path === "/ai-documentation") {
      return "ai-documentation";
    } else if (path === "/medical-transcription") {
      return "medical-transcription";
    } else if (path === "/clinical-summaries") {
      return "clinical-summaries";
    } else if (path === "/pricing") {
      return "pricing";
    } else if (path === "/smart-ehr-integration") {
      return "smart-ehr-integration";
    } else if (path === "/healthcare-analytics") {
      return "healthcare-analytics";
    } else if (path === "/privacy") {
      return "privacy";
    } else if (path === "/terms") {
      return "terms";
    } else if (path === "/compliance") {
      return "compliance";
    } else if (path === "/accessibility") {
      return "accessibility";
    }
    return initialRoute; // Default to home or provided initial route
  });

  // Effect to scroll to top whenever the route changes
  useEffect(() => {
    // Use the utility function for consistent scroll behavior
    scrollToTop();

    // Add a small delay to ensure the component has rendered
    const timer = setTimeout(() => {
      scrollToTop();
    }, 50);

    return () => clearTimeout(timer);
  }, [currentRoute]);

  // Effect to scroll to top on initial mount
  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    // Handle browser navigation
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === "/") {
        setCurrentRoute("home");
      } else if (path === "/ai-documentation") {
        setCurrentRoute("ai-documentation");
      } else if (path === "/medical-transcription") {
        setCurrentRoute("medical-transcription");
      } else if (path === "/clinical-summaries") {
        setCurrentRoute("clinical-summaries");
      } else if (path === "/pricing") {
        setCurrentRoute("pricing");
      } else if (path === "/smart-ehr-integration") {
        setCurrentRoute("smart-ehr-integration");
      } else if (path === "/healthcare-analytics") {
        setCurrentRoute("healthcare-analytics");
      } else if (path === "/privacy") {
        setCurrentRoute("privacy");
      } else if (path === "/terms") {
        setCurrentRoute("terms");
      } else if (path === "/compliance") {
        setCurrentRoute("compliance");
      } else if (path === "/accessibility") {
        setCurrentRoute("accessibility");
      }

      // Scroll to top when navigating via browser back/forward buttons
      setTimeout(() => {
        scrollToTop();
      }, 100);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // const navigate = (route: Route) => {

  //   setCurrentRoute(route);
  //   // Update URL without page reload
  //   window.history.pushState({}, "", route === "home" ? "/" : `/${route}`);
  // };

  const renderRoute = () => {
    switch (currentRoute) {
      case "ai-documentation":
        return <AIDocumentation />;
      case "medical-transcription":
        return <MedicalTranscription />;
      case "clinical-summaries":
        return <ClinicalSummaries />;
      case "pricing":
        return <PricingPageV2 />;
      case "smart-ehr-integration":
        return <SmartEHRIntegration />;
      case "healthcare-analytics":
        return <HealthcareAnalytics />;
      case "privacy":
        return <SecurityPrivacyPolicy />;
      case "terms":
        return <TermsOfUse />;
      case "compliance":
        return <ComplianceFramework />;
      case "accessibility":
        return <AccessibilityStatement />;
      case "home":
        return <LandingPage />;
      default:
        return <LandingPage />;
    }
  };

  return renderRoute();
};

export default Router;
